'use client';

import React, { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Program {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  ageGroup: string;
  duration: string;
  level: string;
  features: string[];
  price: string;
  icon: string;
  detailedDescription: string;
  methodology: string[];
  schedule: string;
  coachName: string;
  coachCertification: string;
  equipment: string[];
  safetyProtocols: string[];
}

interface ProgramDetailModalProps {
  program: Program | null;
  isOpen: boolean;
  onClose: () => void;
  onBookTrial: () => void;
}

const ProgramDetailModal = ({ program, isOpen, onClose, onBookTrial }: ProgramDetailModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !program) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-heading font-bold text-primary">{program.title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors duration-300"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} variant="outline" className="text-text-primary" />
          </button>
        </div>

        <div className="p-6">
          <div className="relative h-64 rounded-lg overflow-hidden mb-6">
            <AppImage
              src={program.image}
              alt={program.alt}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="UserGroupIcon" size={20} variant="solid" className="text-accent" />
                <span className="text-sm font-heading font-semibold text-text-primary">Age Group</span>
              </div>
              <p className="text-text-secondary text-sm">{program.ageGroup}</p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="ClockIcon" size={20} variant="solid" className="text-accent" />
                <span className="text-sm font-heading font-semibold text-text-primary">Duration</span>
              </div>
              <p className="text-text-secondary text-sm">{program.duration}</p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="ChartBarIcon" size={20} variant="solid" className="text-accent" />
                <span className="text-sm font-heading font-semibold text-text-primary">Level</span>
              </div>
              <p className="text-text-secondary text-sm">{program.level}</p>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-heading font-bold text-primary mb-3">Program Overview</h3>
            <p className="text-text-secondary text-sm leading-relaxed">{program.detailedDescription}</p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-heading font-bold text-primary mb-3">Training Methodology</h3>
            <ul className="space-y-2">
              {program.methodology.map((item, index) => (
                <li key={index} className="flex items-start space-x-2 text-sm text-text-secondary">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-heading font-bold text-primary mb-3">Equipment Required</h3>
              <ul className="space-y-2">
                {program.equipment.map((item, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm text-text-secondary">
                    <Icon name="WrenchScrewdriverIcon" size={16} variant="outline" className="text-accent mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-heading font-bold text-primary mb-3">Safety Protocols</h3>
              <ul className="space-y-2">
                {program.safetyProtocols.map((item, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm text-text-secondary">
                    <Icon name="ShieldCheckIcon" size={16} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <Icon name="UserCircleIcon" size={24} variant="solid" className="text-accent" />
              <div>
                <h4 className="text-sm font-heading font-bold text-primary">{program.coachName}</h4>
                <p className="text-xs text-text-secondary">{program.coachCertification}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 mt-3">
              <Icon name="CalendarIcon" size={16} variant="outline" className="text-accent" />
              <span className="text-sm text-text-secondary">{program.schedule}</span>
            </div>
          </div>

          <div className="flex items-center justify-center pt-6 border-t border-border">
            <button
              onClick={onBookTrial}
              className="px-8 py-4 bg-accent text-accent-foreground font-heading font-bold text-lg rounded-md hover:bg-accent/90 transition-all duration-300 flex items-center space-x-2 shadow-hover"
            >
              <span>Book a Trial Session</span>
              <Icon name="ArrowRightIcon" size={20} variant="outline" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramDetailModal;